import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import { UserRole, SupabaseUser } from '../types';

interface UserProfile {
  id: number;
  clinic_id: number | null;
  name: string | null;
  whatsapp: string | null;
  email: string | null;
  role: UserRole;
  permissions: any;
  doctor_id: number | null;
  created_at: string;
  updated_at: string;
}

interface UseUserProfileReturn {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para gerenciar dados do perfil do usuário e implementar estrutura multi-tenant
 * @returns Objeto com dados do usuário, estado de carregamento e funções
 */
export const useUserProfile = (): UseUserProfileReturn => {
  const { authState } = useAuth();
  const { user: supabaseUser } = authState;
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = useCallback(async () => {
    // Se não há usuário autenticado e o authState ainda está carregando, manter loading=true
    if (!supabaseUser?.email) {
      // Só definir loading=false se o authState não estiver mais carregando
      if (!authState.loading) {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Para proprietários, usar dados do AuthContext
      if (authState.userType === 'owner') {
        const ownerProfile: UserProfile = {
          id: parseInt(supabaseUser.id as string) || 0,
          clinic_id: authState.clinic_id,
          name: (supabaseUser as SupabaseUser).user_metadata?.name || supabaseUser.email?.split('@')[0] || null,
          whatsapp: null,
          email: supabaseUser.email,
          role: 'clinic_admin' as UserRole,
          permissions: {
            canCreateClinics: true,
            canManageUsers: true,
            canManageDoctors: true,
            canManageAttendees: true,
            canViewAllClinics: true,
            canManageAppointments: true,
            canViewReports: true,
            canManageSettings: true
          },
          doctor_id: null,
          created_at: supabaseUser.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setUserProfile(ownerProfile);
        setLoading(false);
        return;
      }

      // Para usuários internos, usar dados que já vêm do AuthContext (via API)
      if (authState.userType === 'internal') {
        const userData = authState.user as any;
        
        // Os dados já vêm completos da API no login
        const internalProfile: UserProfile = {
          id: userData.id || userData.internalUser?.id || 0,
          clinic_id: authState.clinic_id || userData.internalUser?.clinic_id || null,
          name: userData.nome || userData.internalUser?.name || userData.name || null,
          whatsapp: userData.telefone || userData.internalUser?.phone || null,
          email: userData.email || supabaseUser.email || userData.internalUser?.email,
          role: (userData.papel || userData.internalUser?.role || userData.role || 'attendee') as UserRole,
          permissions: userData.permissoes || userData.internalUser?.permissions || {},
          doctor_id: userData.doctor_id || null,
          created_at: userData.criadoEm || userData.created_at || new Date().toISOString(),
          updated_at: userData.atualizadoEm || userData.updated_at || new Date().toISOString()
        };
        
        setUserProfile(internalProfile);
        setLoading(false);
        return;
      }

      // Para outros usuários, buscar dados do usuário na API usando o email do auth
      const params = new URLSearchParams({
        email: supabaseUser.email
      });
      
      const response = await apiService.users.getByEmail(params.toString());
      
      if (!response.sucesso) {
        // Se o usuário não existe, criar um registro
        if (response.mensagem?.includes('não encontrado') || response.mensagem?.includes('not found')) {
          const createResponse = await apiService.users.create({
            email: supabaseUser.email,
            name: (supabaseUser as SupabaseUser).user_metadata?.name || null,
            clinic_id: null,
            role: 'attendee',
            permissions: {
              canCreateClinics: false,
              canManageUsers: false,
              canManageDoctors: false,
              canManageAttendees: false,
              canViewAllClinics: false,
              canManageAppointments: true,
              canViewReports: false,
              canManageSettings: false
            },
            doctor_id: null
          });

          if (!createResponse.sucesso) {
            throw new Error(createResponse.mensagem || 'Erro ao criar usuário');
          }
    
          setUserProfile((createResponse as any).dados);
        } else {
          throw new Error(response.mensagem || 'Erro ao buscar usuário');
        }
      } else {
        setUserProfile((response as any).dados);
      }
    } catch (err: any) {
      console.error('❌ useUserProfile: Erro ao carregar perfil:', err);
      setError(err.message || 'Erro ao carregar dados do usuário');
    } finally {

      setLoading(false);
    }
  }, [supabaseUser, authState.userType, authState.clinic_id]);

  useEffect(() => {
    // Só executar fetchUserProfile se o authState não estiver carregando
    if (!authState.loading) {
      fetchUserProfile();
    }
  }, [supabaseUser, fetchUserProfile, authState.loading]);

  // Manter loading=true enquanto o authState estiver carregando
  useEffect(() => {
    if (authState.loading) {
      setLoading(true);
    }
  }, [authState.loading]);

  return {
    userProfile,
    loading,
    error,
    refetch: fetchUserProfile
  };
};

/**
 * Hook para verificar se o usuário tem acesso a uma clínica específica
 * @param clinicId ID da clínica a ser verificada
 * @returns true se o usuário tem acesso, false caso contrário
 */
export const useClinicAccess = (clinicId: number | null): boolean => {
  const { userProfile } = useUserProfile();
  
  if (!userProfile || !clinicId) return false;
  
  // Verificar se o usuário pertence à clínica
  return userProfile.clinic_id === clinicId;
};

/**
 * Hook para obter a clínica do usuário logado
 * @returns ID da clínica do usuário ou null
 */
export const useUserClinic = (): number | null => {
  const { userProfile } = useUserProfile();
  return userProfile?.clinic_id || null;
};